import {
  getPlanDisplayName,
  PLAN_DEFAULT_LIMITS_BY_CODE,
  type BillingStatusDto,
  type OrganisationEntitlementsDto,
  type PlanCode,
} from '@flowqa/shared';

const devPreviewParamKeys = [
  'devPlan',
  'devUsage',
  'devTrialDay',
  'devMember',
  'devMemberGraceDays',
  'devSubscription',
  'devPlanEndsInDays',
  'devResetPlanAlerts',
] as const;

const parsePlanCode = (value: string | null): PlanCode | null => {
  switch (value) {
    case 'trial':
    case 'solo':
    case 'team':
      return value;
    case null:
      return null;
    default:
      return null;
  }
};

const parseBillingStatus = (value: string | null): BillingStatusDto | null => {
  switch (value) {
    case 'none':
    case 'active':
    case 'trialing':
    case 'past_due':
    case 'canceled':
      return value;
    case null:
      return null;
    default:
      return null;
  }
};

const parseNonNegativeInteger = (value: string | null): number | null => {
  if (value === null || value.trim().length === 0) {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return null;
  }
  return parsed;
};

const startOfUtcDay = (value: Date): Date =>
  new Date(Date.UTC(value.getUTCFullYear(), value.getUTCMonth(), value.getUTCDate()));

const addDays = (value: Date, days: number): Date => {
  const next = new Date(value);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const cloneDate = (value: Date | null): Date | null => (value === null ? null : new Date(value));

const cloneEntitlements = (
  entitlements: OrganisationEntitlementsDto,
): OrganisationEntitlementsDto => ({
  ...entitlements,
  plan: {
    ...entitlements.plan,
    limits: { ...entitlements.plan.limits },
    features: { ...entitlements.plan.features },
  },
  subscription: {
    ...entitlements.subscription,
    trialEndsAt: cloneDate(entitlements.subscription.trialEndsAt),
    planEndsAt: cloneDate(entitlements.subscription.planEndsAt),
    scheduledPlanEffectiveAt: cloneDate(entitlements.subscription.scheduledPlanEffectiveAt),
  },
  addOns: { ...entitlements.addOns },
  usage: {
    ...entitlements.usage,
    periodStart: new Date(entitlements.usage.periodStart),
    periodEnd: new Date(entitlements.usage.periodEnd),
    executionDailyBreakdown: entitlements.usage.executionDailyBreakdown.map((entry) => ({
      ...entry,
      day: new Date(entry.day),
    })),
  },
  access: {
    ...entitlements.access,
    memberGraceEndsAt: cloneDate(entitlements.access.memberGraceEndsAt),
  },
  trialEndsAt: cloneDate(entitlements.trialEndsAt),
  planEndsAt: cloneDate(entitlements.planEndsAt),
});

const resolveUsageCount = (
  value: string | null,
  executionLimit: number | null,
  fallback: number,
): number => {
  if (value === null || value.trim().length === 0) {
    return fallback;
  }

  if (executionLimit !== null && executionLimit > 0) {
    if (value === '80') {
      return Math.floor(executionLimit * 0.8);
    }
    if (value === '90') {
      return Math.floor(executionLimit * 0.9);
    }
    if (value === '100' || value === 'limit') {
      return executionLimit;
    }
  }

  const parsed = parseNonNegativeInteger(value);
  return parsed ?? fallback;
};

const resolveMemberLabel = (value: string | null): string | null => {
  switch (value) {
    case 'primary':
      return 'Primary member';
    case 'grace':
      return 'Solo grace window';
    case 'locked':
      return 'Solo locked member';
    case null:
      return null;
    default:
      return null;
  }
};

export const hasDevPlanOverrides = (searchParams: URLSearchParams): boolean =>
  import.meta.env.DEV && devPreviewParamKeys.some((key) => searchParams.has(key));

export const getDevPlanOverrideSummary = (searchParams: URLSearchParams): string[] => {
  if (!hasDevPlanOverrides(searchParams)) {
    return [];
  }

  const summary: string[] = [];
  const planCode = parsePlanCode(searchParams.get('devPlan'));
  if (planCode !== null) {
    summary.push(`Plan ${getPlanDisplayName(planCode)}`);
  }

  const usage = searchParams.get('devUsage');
  if (usage !== null && usage.length > 0) {
    summary.push(`Usage ${usage}`);
  }

  const trialDay = parseNonNegativeInteger(searchParams.get('devTrialDay'));
  if (trialDay !== null) {
    summary.push(`Trial day ${trialDay}`);
  }

  const memberLabel = resolveMemberLabel(searchParams.get('devMember'));
  if (memberLabel !== null) {
    summary.push(memberLabel);
  }

  const subscriptionStatus = parseBillingStatus(searchParams.get('devSubscription'));
  if (subscriptionStatus !== null) {
    summary.push(`Subscription ${subscriptionStatus}`);
  }

  return summary;
};

export const applyDevPlanOverrides = (
  entitlements: OrganisationEntitlementsDto | null,
  searchParams: URLSearchParams,
  now: Date = new Date(),
): OrganisationEntitlementsDto | null => {
  if (!import.meta.env.DEV || entitlements === null || !hasDevPlanOverrides(searchParams)) {
    return entitlements;
  }

  const next = cloneEntitlements(entitlements);
  const nowDay = startOfUtcDay(now);
  const overridePlanCode = parsePlanCode(searchParams.get('devPlan'));
  const planCode = overridePlanCode ?? next.plan.code;

  if (overridePlanCode !== null) {
    next.plan.code = overridePlanCode;
    next.plan.name = getPlanDisplayName(overridePlanCode);
    next.plan.limits = { ...PLAN_DEFAULT_LIMITS_BY_CODE[overridePlanCode] };
    next.addOns = { deliveryEnabled: false, operationsEnabled: false, codeEnabled: false };
  }

  if (planCode === 'trial') {
    const trialDay = parseNonNegativeInteger(searchParams.get('devTrialDay')) ?? 0;
    const trialStart = addDays(nowDay, -trialDay);
    const trialEndsAt = addDays(trialStart, 14);
    next.usage.periodType = 'monthly';
    next.usage.periodStart = trialStart;
    next.usage.periodEnd = trialEndsAt;
    next.usage.executionLimit = next.plan.limits.executionLimitMonthly;
    next.subscription.provider = 'none';
    next.subscription.planSource = 'internal';
    next.subscription.status = 'trialing';
    next.subscription.trialEndsAt = trialEndsAt;
    next.subscription.planEndsAt = null;
    next.trialEndsAt = trialEndsAt;
    next.planEndsAt = null;
    next.access.isPrimaryMember = true;
    next.access.currentUserWriteAccess = true;
    next.access.memberGraceEndsAt = null;
    next.access.primaryMemberUserId = next.access.primaryMemberUserId ?? 'dev-primary-member';
  } else {
    const planEndsInDays = parseNonNegativeInteger(searchParams.get('devPlanEndsInDays')) ?? 30;
    const planStart = nowDay;
    const periodEnd = addDays(planStart, 30);
    const planEndsAt = addDays(planStart, planEndsInDays);
    next.usage.periodType = 'monthly';
    next.usage.periodStart = planStart;
    next.usage.periodEnd = periodEnd;
    next.usage.executionLimit = next.plan.limits.executionLimitMonthly;
    next.subscription.provider = 'none';
    next.subscription.planSource = 'internal';
    next.subscription.status = 'active';
    next.subscription.trialEndsAt = null;
    next.subscription.planEndsAt = planEndsAt;
    next.trialEndsAt = null;
    next.planEndsAt = planEndsAt;
    next.access.isPrimaryMember = true;
    next.access.currentUserWriteAccess = true;
    next.access.memberGraceEndsAt = null;
    next.access.primaryMemberUserId = next.access.primaryMemberUserId ?? 'dev-primary-member';

    if (planCode === 'solo') {
      const memberMode = searchParams.get('devMember');
      if (memberMode === 'grace') {
        const graceDays = parseNonNegativeInteger(searchParams.get('devMemberGraceDays')) ?? 3;
        next.access.isPrimaryMember = false;
        next.access.currentUserWriteAccess = true;
        next.access.memberGraceEndsAt = addDays(nowDay, graceDays);
      } else if (memberMode === 'locked') {
        next.access.isPrimaryMember = false;
        next.access.currentUserWriteAccess = false;
        next.access.memberGraceEndsAt = null;
      }
    }
  }

  next.usage.executionCount = resolveUsageCount(
    searchParams.get('devUsage'),
    next.usage.executionLimit,
    next.usage.executionCount,
  );

  const subscriptionStatus = parseBillingStatus(searchParams.get('devSubscription'));
  if (subscriptionStatus !== null) {
    next.subscription.status = subscriptionStatus;
  }

  const executionLimit = next.usage.executionLimit;
  if (executionLimit !== null) {
    next.usage.executionCount = Math.min(next.usage.executionCount, executionLimit);
  }

  next.usage.executionDailyBreakdown = [
    {
      day: new Date(nowDay),
      executionCount: next.usage.executionCount,
      executionRunCount: next.usage.executionCount,
      suiteRunCount: 0,
      testRunCount: 0,
    },
  ];

  return next;
};
